import '../../config';
import '../../dev';
import './unit-editor-component';
import * as api from '../../api';
import AddSlantedShelfTool from './tools/add-slanted-shelf-tool';
import AddTool, { AddToolContext } from './tools/add-tool';
import ClosetUnitView from '../../views/closet-unit-view';
import CommandManager from '../../managers/command-manager';
import DefaultsManager from '../../managers/defaults-manager';
import DomainModel from '../../domain-model';
import DoorView from '../../views/door-view';
import InputChangeEvent from './components/input-change-event';
import ModelHistory from './model-history';
import MultiTool from './tools/multi-tool';
import SelectionManager from './selection-manager';
import UnitEditorComponent from './unit-editor-component';
import logging from '../../logging';
import longestDimension from '../../view3d/longest-dimension';
import mfac from '../../material-factory';
import omitDefaults from '../../json/omit-defaults';
import propertiesClassLookup from './properties/map';
import { AddCubbyTool } from './tools/add-cubby-tool';
import { AddDoorTool } from './tools/add-door-tool';
import { AddDrawerTool } from './tools/add-drawer-tool';
import { AddGenericAssetTool } from './tools/add-generic-asset-tool';
import { AddParametricPartTool } from './tools/add-parametric-part-tool';
import { ClosetUnit, Material, Room, UnitEditorAssetBin } from '../../domain';
import { DefaultsConfig, defaultsDefaults } from '../../managers/defaults-config-options';
import { Object3D } from 'three';
import { Picker } from '../../view3d/picker';
import { ToggleRenderPipeline } from './render-pipeline';
import { UnitMeasurements } from './unit-measurements';
import { camel2words } from '../../text/lettercase';
import { canDeleteModel, deleteModel } from '../../controllers/delete-model';
import { clone, OptionalProperties, update } from "../../json/optional-properties";
import { getParentView } from '../../controllers/base-view-controllers';
import { init as initViewer } from '../viewer/viewer-app';
import { layoutClosetUnit } from '../../controllers/closet-unit-controllers';
import { layoutSection } from '../../controllers/section-controllers';
import { loadString } from '../../utils/load-string';
import { makeMainLights } from '../../view3d/make-main-lights';
import { ready } from '../../html/ready';
import { renderObjectURL } from '../../view3d/thumbnail-renderer';
import { saveBuffer } from '../../utils/save-buffer';
import { setAvailableSlots } from '../../measurement';
import { setHandleHeight } from '../../controllers/door-controllers';
import ReadySignal from '../../control-flow/ready-signal';

const log = logging('UnitEditorApp');

type DotNetObjectReference = {
    invokeMethodAsync<T>(name: string, ...args: unknown[]): Promise<T>;
}

export const init = (containerId: string, model: ClosetUnit | object, assets: UnitEditorAssetBin[], dotNetRef: DotNetObjectReference, options?: UnitEditorOptions) => {
    if(!(model instanceof ClosetUnit)) {
        model = ClosetUnit.fromJSON(model);
    }
    const app = new UnitEditorApp(containerId, model as ClosetUnit, assets, dotNetRef, options);
    ready(() => app.start());

    return app;
}

type UnitEditorConfig = {
    standardHeights: number[],
    defaults: DefaultsConfig,
}

type UnitEditorOptions = OptionalProperties<UnitEditorConfig>;

const unitEditorDefaults: UnitEditorConfig = {
    standardHeights: [0.375, 2.0625, 3.3125, 4.5625, 4.625, 5.8125, 7.0625, 8.375, 9.625, 10.875, 12.126, 13.386, 14.646, 15.125, 15.906, 17.165, 18.425, 19.685, 20.945, 22.205, 23.465, 24.724, 25.984, 26.5, 27.244, 28.504, 29.764, 31.024, 32.283, 33.543, 34.803, 36.063, 37.323, 38.583, 39.843, 41.103, 42.362, 43.622, 44.882, 46.142, 47.402, 48.662, 49.921, 51.181, 52.441, 53.701, 54.961, 56.221, 57.481, 58.74, 60, 61.26, 62.52, 63.78, 65.04, 66.299, 67.559, 68.819, 70.079, 71.339, 72.599, 73.858, 75.118, 76.378, 77.638, 78.898, 80.158, 81.418, 82.677, 83.937, 85.197, 86.457, 87.717, 88.977, 90.236, 91.496, 92.756, 94.016, 95.276, 96.536, 97.796, 99.056, 100.316, 101.576, 102.836, 104.096, 105.356, 106.616, 107.876, 109.136, 110.396, 111.656, 112.916, 114.176, 115.436, 116.696, 117.956, 119.216, 120],
    defaults: defaultsDefaults,
};

type DoorAppearance = "solid" | "transparent" | "invisible";

/**
 * The UnitEditorApp's start() method is the main entrypoint for the unit
 * editor.
 */
export class UnitEditorApp {

    model: ClosetUnit;
    view: ClosetUnitView;
    dotNetRef: DotNetObjectReference;

    assetBins: UnitEditorAssetBin[];

    selection: SelectionManager = new SelectionManager();
    history: ModelHistory<ClosetUnit>;
    defaults: DefaultsManager;

    private commands = new CommandManager('unit-editor');

    pipeline: ToggleRenderPipeline = new ToggleRenderPipeline();
    picker: Picker = new Picker(this.pipeline.ortho.camera, this.pipeline.canvas);
    config: UnitEditorConfig = clone(unitEditorDefaults);

    addTools: AddTool[] = [];

    private multitool: MultiTool = new MultiTool(this);
    private component: UnitEditorComponent;
    private measurements: UnitMeasurements;
    private showDoors: DoorAppearance = "solid";

    constructor(containerId: string, model: ClosetUnit, assets: UnitEditorAssetBin[] = [], dotNetRef: DotNetObjectReference, options?: UnitEditorOptions) {

        this.model = model;
        this.dotNetRef = dotNetRef;
        this.assetBins = assets;

        this.history = new ModelHistory(ClosetUnit, model);
        this.history.onUndo.add(this.restoreModel);
        this.history.onRedo.add(this.restoreModel);

        this.setupCommands();

        update(this.config, options);
        setAvailableSlots(this.config.standardHeights);

        this.component = new UnitEditorComponent(assets);
        this.component.addEventListener('viewport-update', () => {
            this.pipeline.update = true;
        });
        this.measurements = new UnitMeasurements(
            this.view, this.pipeline.ortho.camera, {});
        document.getElementById(containerId).append(this.component);

        this.component.onSubmit.add(this.handleSubmit);
        this.component.onSaveUnit.add(this.handleSaveUnit);
        this.component.onDoorAppearance.add(how => {
            this.showDoors = how;
            this.refresh();
        });
    }

    async start() {
        log.debug('UnitEditorApp.start()');


        layoutClosetUnit(this.model);
        layoutSection(this.model.content);
        this.model.commit(true);

        mfac.onLoad.add(() => {
            this.pipeline.update = true;
        });

        this.defaults = new DefaultsManager(this.config.defaults);


        this.component.viewportContainer.append(this.measurements.canvas);
        this.component.viewportContainer.append(this.pipeline.canvas);
        this.pipeline.canvas.classList.add('viewport');

        this.pipeline.onBeforeRender.add(() => {
            layoutClosetUnit(this.model);
            layoutSection(this.model.content);
            updateHandleHeight(this.view);
        });

        // The following is a bunch of miscellany that I'd love to feel like it
        // had a proper home, but there's just not enough of it to really
        // bother with yet.

        const lightGroup = makeMainLights(Math.PI/4, -Math.PI/4, []);
        this.pipeline.scene.add(lightGroup);

        const btnUndo = this.component.btnUndo;
        const btnRedo = this.component.btnRedo;
        const updateUndoRedoButtons = () => {
            if(this.history.canUndo()) {
                btnUndo.classList.remove('disabled');
            } else {
                btnUndo.classList.add('disabled');
            }
            if(this.history.canRedo()) {
                btnRedo.classList.remove('disabled');
            } else {
                btnRedo.classList.add('disabled');
            }
            btnUndo.setAttribute('title', this.history.getUndoDescription());
            btnRedo.setAttribute('title', this.history.getRedoDescription());
        };
        updateUndoRedoButtons();
        this.history.onChange.add(updateUndoRedoButtons);

        // We must enable the picker before any tools so that it updates the
        // hit location before any tools query for intersected objects
        this.picker.enable();

        this.multitool.enable();
        this.multitool.onModeChange.add((mode) => {
            switch(mode) {
                case MultiTool.FRONT:
                    this.measurements.start();
                    break;
                case MultiTool.PERSPECTIVE:
                    this.measurements.stop();
                    break;
            }
        });
        this.multitool.panzoom.onChange.add(() => {
            this.pipeline.update = true;
            this.measurements.loop.update();
        });
        this.multitool.move.onMove.add(() => {
            this.pipeline.update = true;
            this.measurements.loop.update();
        });

        this.selection.onChange.add(() => {
            this.updateProperties()
            this.pipeline.update = true;
        });

        const ctx: AddToolContext = {
            camera: this.pipeline.ortho.camera,
            canvas: this.pipeline.canvas,
            defaults: this.defaults,
            picker: this.picker,
            scene: this.pipeline.scene,
        }

        for(const bin of this.assetBins) {
            const child = this.component.addBin(bin);
            for(const item of bin.items) {
                const itemElt = child.addItem(item);

                if(item.cubby) {
                    const tool = new AddCubbyTool(
                        item.cubby, itemElt, ctx);
                    tool.onSuccess.add(() => this.commit(null, 'add cubby'));
                    this.addTools.push(tool);
                }
                else if(item.door) {
                    const tool = new AddDoorTool(
                        item.door, itemElt, this.selection, ctx);
                    tool.onSuccess.add(() => {
                        this.commit(null, 'add door');
                        if(this.showDoors == 'invisible') {
                            this.component.setDoorAppearance('solid');
                            this.showDoors = 'solid';
                            this.refresh();
                        }
                    });
                    this.addTools.push(tool);
                }
                else if(item.drawer) {
                    const tool = new AddDrawerTool(
                        item.drawer, itemElt, ctx);
                    tool.onSuccess.add(() => this.commit(null, 'add drawer'));
                    this.addTools.push(tool);

                }
                else if(item.genericAsset) {
                    const tool = new AddGenericAssetTool(
                        item.genericAsset, itemElt, ctx);
                    tool.onSuccess.add((asset) => {
                        this.commit(null, `add ${camel2words(asset.filename)}`);
                    });
                    this.addTools.push(tool);
                }
                else if(item.parametricPart) {
                    const tool = new AddParametricPartTool(
                        item.parametricPart, itemElt, ctx);
                    tool.onSuccess.add(() => this.commit(
                        null, `add ${camel2words(item.parametricPart.name)}`));
                    this.addTools.push(tool);
                } else if(item.slantedShelf) {
                    const tool = new AddSlantedShelfTool(
                        item.slantedShelf, itemElt, ctx);
                    tool.onSuccess.add(() => this.commit(
                        null, 'add slanted shelf'));
                    this.addTools.push(tool);
                }
            }
        }

        for(let tool of this.addTools) {
            tool.enable();
            tool.onDisplayChange.add(() => this.pipeline.update = true);
            tool.onSuccess.add(() => this.refresh());
            tool.onSuccess.add(this.measurements.loop.update);
        };

        // TODO: add a keymap manager
        window.addEventListener('keyup', (event) => {
            const
                isDelete = event.key == 'Delete' || event.key == 'Backspace',
                hasSelection = this.selection.view != null,
                isntInsideInput = document.activeElement == document.body,
                isntRootElement = this.selection.model != this.model;

            if(isDelete && hasSelection && isntInsideInput && isntRootElement) {
                UnitEditorApp.do('delete-selection');
            } else if(event.key == 'z' && event.ctrlKey) {
                UnitEditorApp.do('undo');
            } else if(event.key == 'y' && event.ctrlKey) {
                UnitEditorApp.do('redo');
            }
        });

        this.component.propertiesContainer.addEventListener('change', (event) => {
            this.pipeline.update = true;
            if(event instanceof InputChangeEvent) {
                if(event.detail.name == 'set-door-hinge') {
                    this.commit(event.detail.name, 'change door hinge');
                    this.refresh();
                }
                if(event.detail.name == 'set-door-type') {
                    this.commit(event.detail.name, 'change door type');
                    this.refresh();
                }
            }
        });

        // End of the bothersome miscellany

        await this.refresh();
    }

    /**
     * Called any time there's an update to the model that changes its
     * structure in a way such that it's simpler to rebuild the whole 3D object
     * rather than try to change all the fiddly details. The pipeline is
     * stopped for the duration of the refresh in an attempt to make sure that
     * it doesn't blink on screen a bunch.
     */
    async refresh() {
        this.pipeline.stop();
        this.measurements.stop();

        try {
            this.model.unbindAll();

            const newObject = new ClosetUnitView(this.model, {});

            this.view?.removeFromParent();
            this.picker.unwatchAll();

            this.view = newObject;

            const onMaterialsReady = new ReadySignal('onMaterialsReady');
            this.view.onReady.depends(onMaterialsReady);
            mfac.onLoad.once(onMaterialsReady.trigger);

            // Creating all the materials here should cause any network
            // requests to be initiated now. This is intended to improve the
            // accuracy of the three.js LoadingManager reporting that
            // everything has completed loading. That event can be reported
            // earlier than we want when material loading is initiated after
            // model loading completes.
            this.model.traverse((model: DomainModel) => {
                if(model instanceof Material) {
                    try {
                        mfac.makefrom(model as Material);
                    } catch(error) {
                        console.debug(error);
                    }
                }
            });

            this.measurements.view = this.view;
            this.measurements.model = this.model;
            this.pipeline.scene.add(this.view);

            this.view.traverse(child => {
                if(child.userData.isSelectable || child.userData.isMovable) {
                    // when we're showing the front, we watch everything
                    if(this.showDoors == "solid") {
                        this.picker.watch(child);
                    }
                    // if the front is invisible, we do not watch doors
                    else {
                        const parentView = getParentView(child);
                        if(! (parentView instanceof DoorView)) {
                            this.picker.watch(child);
                        }
                    }
                }
                if(child instanceof DoorView)
                {
                    child.visible = this.showDoors != "invisible";
                    if(this.showDoors == "transparent") {
                        child.model.opacity = 0.2;
                    } else {
                        child.model.opacity = 1; 
                    }
                }
            });

            this.selection.clear();
            this.updateProperties();

            await this.view.onReady.wait(1000);
        } finally {
            this.pipeline.start();
            this.pipeline.update = true;
            this.measurements.start();
            this.measurements.loop.update();
        }
    }

    /**
     * Simple global commands can be dispatched on the Window object and will
     * be picked up here by the application. See the CommandButton and
     * CommandSelect WebComponents for example usage.
     */
    setupCommands() {
        this.command('undo', () => {
            this.history.undo();
        });

        this.command('redo', () => {
            this.history.redo();
        });

        this.command('pipeline-update', () => {
            this.pipeline.update = true;
        });

        this.command('refresh', () => {
            this.refresh();
        });

        this.command('view-front', () => {
            this.pipeline.view("ortho");
            this.multitool.mode = MultiTool.FRONT;
            for(let tool of this.addTools) { tool.enable() };
        });

        this.command('view-3d', () => {
            this.pipeline.view("perspective");
            this.selection.clear();
            this.multitool.mode = MultiTool.PERSPECTIVE;
            for(let tool of this.addTools) { tool.disable() };
        });

        this.command('load', (choice: string) => {
            api.load(choice)
                .then((newModel) => {
                    this.history.add(null, `load ${choice}`, newModel);
                    this.model = newModel;
                    this.model.traverse((model: DomainModel) => {
                        this.defaults.reset(model);
                    });
                    this.refresh();
                })
                .catch(console.error);
        });

        this.command('delete-selection', () => {
            // if the user has pressed the delete key, then we may be trying to
            // delete something undeletable. Rather than adding the check
            // there, I've added it here in case anything else ever issues the
            // delete-selection command in the future.
            if(canDeleteModel(this.selection.model)) {
                const description = deleteModel(this.selection.model);
                this.commit(undefined, 'delete ' + description);
                this.refresh();
            }
        });

        this.command('show-doors', (showDoors: typeof this.showDoors) => {
            this.showDoors = showDoors;
            this.refresh();
        });

        this.command('show-heights', (showHeights: boolean) => {
            this.measurements.setShowHeights(showHeights);
        });

        this.command('launch-viewer', this.launchViewer);

        this.command('upload-json', this.uploadJSON);

        this.command('download-json', this.downloadJSON);
    }

    private updateProperties = () => {
        const container = this.component.propertiesContainer;
        if(!container) return;
        container.innerHTML = '';

        if(this.selection.model) {
            let elt: HTMLElement;
            const PropertiesClass = propertiesClassLookup(
                this.selection.model.constructor as any);
            if(PropertiesClass) {
                elt = new PropertiesClass(this.selection.model);
                container.append(elt);
            }
            if(canDeleteModel(this.selection.model)) {
                this.component.btnDeleteSelection.style.display = "";
            } else {
                this.component.btnDeleteSelection.style.display = 'none';
            }
        } else {
            container.replaceChildren();
            this.component.btnDeleteSelection.style.display = 'none';
        }
    }

    restoreModel = (model: ClosetUnit) => {
        this.model = model;
        this.refresh();
    };

    /**
     * Launch the viewer overlay for inspecting the scene.
     */
    launchViewer = () => {
        const
            height = Math.max(96, this.model.height),
            width = this.model.width + 1.5,
            room = Room.fromJSON({
            walls: [
                {
                    heights: [
                        {x: 0, height},
                        {x: width, height},
                    ],
                    visible: false,
                    closetUnits: [
                        this.model.toStateObject(),
                    ]
                }
            ]
        });
        this.view.visible = false;
        const viewer = window["viewer"] =
            initViewer(room, this.pipeline.perspective.camera, this.pipeline.scene);
        viewer.onExit.add(() => {
            this.view.visible = true;
            viewer.view.removeFromParent();
            this.refresh();
        });
        viewer.onStart.add(() => {
            viewer.commands.trigger('update');;
            viewer.refresh();
        });
    }

    /**
     * Download the model JSON for testing.
     */
    downloadJSON = () => {
        const
            encoder = new TextEncoder(),
            str = JSON.stringify(this.model.toStateObject(), omitDefaults, 4),
            data = encoder.encode(str);

        saveBuffer(data.buffer, 'unit.json');
    }

    /**
     * To support testing, prompts for a file upload that replaces the JSON of
     * the current unit. Note that this doesn't implement undo/redo currently.
     */
    uploadJSON = async () => {
        const str = await loadString();
        this.model = ClosetUnit.fromJSON(JSON.parse(str));
        this.refresh();
    }

    /**
     * Register a handler for an application command. This could be done by an
     * app implementation itself, as with the undo/redo and pipeline-update
     * commands implemented in the base Application class, or they could be
     * implemented by any component of the application.
     *
     * Since commands as a concept are global and have a static lifetime, there
     * is no way to remove a command once it's registered. If you need
     * something like that, probably best to use an event bus of some sort,
     * e.g. the window's dispatchEvent() method.
     */
    command(action: string, handler: (...args: unknown[]) => unknown): void {
        this.commands.on(action, handler);
    }

    /**
     * Call `Application.do('command-name', ...)` to send a command to the
     * application instance. These commands are handled by functions registered
     * with the Application instance's command() method.
     */
    static do(action: string, ...args: any[]): void {
        CommandManager.do('unit-editor', action, ...args);
    }

    /**
     * This is the primary interface to the command history based on
     * serializing the root domain model. It has a side-effect: it recursively
     * commits changes to app.model. In fact, if changes are commited outside
     * of this method, then this method won't work correctly.
     * @param name If it's undefined, then repreated commits will not merge.
     * Otherwise, repeated commits under the same name will merge so that they
     * appear as a single undo/redo frame.
     * @param description A user visible string describing the action taken to
     * generate this unde frame, e.g. 'Add drawer'
     */
    commit(name: string | undefined, description: string): void {
        this.model.commit(true);
        this.history.add(name, description, this.model);
        this.pipeline.update = true;
    }

    setLogLevel(module: string, level: string) {
        const mod = logging(module);
        switch(level.toUpperCase()) {
            case 'TRACE':
                mod.level = log.TRACE;
                break;
            case 'DEBUG':
                mod.level = log.DEBUG;
                break;
            case 'INFO':
                mod.level = log.INFO;
                break;
            case 'WARN':
                mod.level = log.WARN;
                break;
            case 'ERROR':
                mod.level = log.ERROR;
                break;
            default:
                log.error(`Unconvertible log level '${level}', expected TRACE, DEBUG, INFO, WARN, or ERROR (case insensitive).`);
        }
    }

    handleSubmit = async () => {
        // TODO: need to disable the buttons and show a waiting indicator

        type SubmitResult = {
            isSuccess: boolean;
            errors: string[];
            validationErrors: { errorMessage: string }[];
        }

        if(this.dotNetRef) {
            const result: SubmitResult =
                await this.dotNetRef.invokeMethodAsync('Submit', this.model.toStateObject());
            if(result.isSuccess) {
                location.href = "/Closet-Organizer-Build.aspx";
            } else {
                console.error('Problem during submission');
                console.error('Errors:\n', result.errors.join('\n'));
                console.error('Validation errors:\n',
                              result.validationErrors.map(e => e.errorMessage).join('\n'));
            }
        }
    }

    handleSaveUnit = async () => {
        // NB: see here for how to get the bytes of the object URL:
        // https://www.xjavascript.com/blog/how-to-get-a-file-or-blob-from-an-object-url/
        const
            room = Room.fromJSON({
                walls: [
                    {
                        heights: [
                            {x: 0, height: 96},
                            {x: this.model.width + 1.5, height: 96},
                        ],
                        visible: true,
                        closetUnits: [
                            this.model.toStateObject(),
                        ]
                    }
                ]
            }),
            [_, promise] = renderObjectURL(room, 250, 600),
            url = await promise;
        window.open(url);
    }
}
export default UnitEditorApp;


const updateHandleHeight = (scene: Object3D) => {
    scene.traverse(obj => {
        if(obj instanceof DoorView) {
            if(obj.model.hinge != 'top' && obj.model.hinge != 'bottom') {
                for(const handle of obj.model.handles) {
                    let height: number;
                    if(obj.single) {
                        height = longestDimension(obj.single.handle.handle);
                    } else {
                        height = longestDimension(obj.double.left.handle.handle);
                    }

                    setHandleHeight(obj.model.position.y, handle, height);
                }
            }
        }
    });
}
